// export interface ISlugProxyStorage {
//
// }

export interface BaseProxyStorage<Model extends Record<Keys, any>, Keys extends string> {
  keys: () => Promise<Keys[]>
  setItem: <Key extends Keys>(key: Key, value?: Model[Key]) => Promise<void>
  getItem: <Key extends Keys>(key: Key) => Promise<Model[Key] | undefined>
}

export interface IProxyStorage<Model extends Record<Keys, any>, Keys extends string>
  extends BaseProxyStorage<Model, Keys> {
  removeItem?: (key: Keys) => Promise<void>
  batchSet?: (data: Partial<Model>) => Promise<void>
  batchGet?: <Key extends Keys>(keys: Key[]) => Promise<Pick<Model, Extract<keyof Model, Key>>>
  batchRemove?: (keys: Keys[]) => Promise<void>
  clear?: () => Promise<void>
}

export type ProxyStorageAdapter<OriginStorage> = (storage: OriginStorage) => IProxyStorage<Record<string, any>, string>

type ProxyStorage<Model extends Record<Keys, any>, Keys extends string> = Required<IProxyStorage<Model, Keys>>

export interface CreateProxyStorageOptions<OriginStorage = any> {
  ns?: string // 命名空间

  // TODO: 目前 OriginStorage 的约束似乎没起作用 -> 可能是 CreateProxyStorageOptions | Record<string, any> 导致？
  adapter?: ProxyStorageAdapter<OriginStorage>
  storage: OriginStorage
}

// === utils ===

const isOptions = (opts: any): opts is CreateProxyStorageOptions => !!opts.storage;

const nk = <T extends string>(ns: string, k: T): T => (ns + k) as T;

// === adapter ===

export const simpleStorageAdapter: ProxyStorageAdapter<Record<string, any>> = storage => ({
  async keys() {
    return Object.keys(storage);
  },
  async setItem(key: string, value?: any) {
    // eslint-disable-next-line no-param-reassign
    storage[key] = value;
  },
  async getItem(key: string) {
    return storage[key];
  },

  // 防止 keys() 出错
  async removeItem(key: string) {
    // eslint-disable-next-line no-param-reassign
    delete storage[key];
  },
});

export const webStorageAdapter: ProxyStorageAdapter<Storage> = storage => ({
  async keys() {
    return Array(storage.length)
      .fill(undefined)
      .map((_, i) => storage.key(i))
      .filter(v => !!v) as string[];
  },
  async setItem(key: string, value?: any) {
    if (value === undefined) {
      // return this.removeItem!(key);
      storage.removeItem(key);
      return;
    }
    storage.setItem(key, JSON.stringify(value));
  },
  async getItem(key: string) {
    const value = storage.getItem(key);
    return value ? JSON.parse(value) as any : undefined;
  },
  // async removeItem(key: string) {
  //   storage.removeItem(key);
  // },
  async clear() {
    storage.clear();
  },
});

// === core ===

// eslint-disable-next-line max-len
export function createProxyStorage<Model extends Record<Keys, any> = any, Keys extends string = keyof Model & string>(opts: CreateProxyStorageOptions | Record<string, any>): ProxyStorage<Model, Keys> {
  const options = isOptions(opts) ? opts : { storage: opts };
  const {
    ns: namespace = '',
    adapter = simpleStorageAdapter,
    storage,
  } = options;
  const tmp = namespace
    .split('.')
    .filter((v: any) => !!v)
    .join('.');
  const ns = !!tmp ? `${tmp}.` : '';
  const proxy = adapter(storage) as IProxyStorage<Model, Keys>;
  return {
    async keys() {
      return (await proxy.keys()).filter(k => k.startsWith(ns)).map(key => key.replace(ns, '') as Keys);
    },
    async setItem<Key extends Keys>(key: Key, value?: Model[Key]) {
      return await proxy.setItem(nk(ns, key), value);
    },
    async getItem<Key extends Keys>(key: Key) {
      return await proxy.getItem<Key>(nk(ns, key));
    },

    async removeItem(key: Keys) {
      if (proxy.removeItem) {
        return await proxy.removeItem(nk(ns, key));
      }
      await this.setItem(nk(ns, key), undefined);
    },
    async batchSet(data: Partial<Model>) {
      const entries = Object.entries(data);
      if (proxy.batchSet) {
        return await proxy.batchSet(Object.fromEntries(entries.map(([key, value]) => [nk(ns, key), value])) as any);
      }
      await Promise.all(entries.map(async ([key, value]) => await this.setItem(key as any, value as any)));
    },
    async batchGet<Key extends Keys>(keys: Key[]) {
      if (proxy.batchGet) {
        return await proxy.batchGet<Key>(keys.map(key => nk(ns, key)));
      }
      return Object.fromEntries((await Promise.all(keys.map(async key => [key, await this.getItem(key)]))));
    },
    async batchRemove(keys: Keys[]) {
      if (proxy.batchRemove) {
        return await proxy.batchRemove(keys.map(key => nk(ns, key)));
      }
      await Promise.all(keys.map(async key => await this.removeItem(key)));
    },
    async clear() {
      if (proxy.clear && ns === '') {
        return await proxy.clear();
      }
      await this.batchRemove(await this.keys());
    },
  };
};
